import type { Image } from '@content-scheduler/shared/render';
import type { ReactNode } from 'react';
import { DismissButton } from './DismissButton';
import layouts from './layouts.module.css';
import { Media } from './Media';

export { layouts };

type Props = { image: Image; onDismiss: (() => void) | null; children: ReactNode };

const hasImage = (image: Image) => Boolean(image?.src.trim());

/** Optional image banner on top, then the content, centered or left aligned. */
export function CardLayout({
    image,
    onDismiss,
    children,
    align = 'center',
}: Props & { align?: 'center' | 'start' }) {
    return (
        <article
            className={layouts.card}
            data-has-media={hasImage(image) ? '' : undefined}
            data-dismissible={onDismiss ? '' : undefined}
        >
            <Media image={image} className={layouts.cardMedia} />
            <div className={layouts.cardBody} data-align={align}>
                {children}
            </div>
            {onDismiss && <DismissButton onClick={onDismiss} />}
        </article>
    );
}

/** Image beside the content from 560px wide; stacked below that. */
export function SplitLayout({ image, onDismiss, children }: Props) {
    return (
        <div
            className={layouts.split}
            data-has-media={hasImage(image) ? '' : undefined}
            data-dismissible={onDismiss ? '' : undefined}
        >
            <Media image={image} className={layouts.splitMedia} />
            <div className={layouts.splitBody}>{children}</div>
            {onDismiss && <DismissButton onClick={onDismiss} />}
        </div>
    );
}

/** Content over a full-bleed image, with a scrim that keeps white text readable. */
export function OverlayLayout({ image, onDismiss, children }: Props) {
    return (
        <div className={layouts.overlay} data-dismissible={onDismiss ? '' : undefined}>
            <Media image={image} className={layouts.overlayMedia} />
            <div className={layouts.overlayBody}>{children}</div>
            {onDismiss && <DismissButton onClick={onDismiss} />}
        </div>
    );
}

/** One slim row: text on the left, countdown and buttons on the right. */
export function BarLayout({
    text,
    actions,
    onDismiss,
}: {
    text: ReactNode;
    actions: ReactNode;
    onDismiss: (() => void) | null;
}) {
    return (
        <div className={layouts.bar} data-dismissible={onDismiss ? '' : undefined}>
            <div className={layouts.barText}>{text}</div>
            <div className={layouts.barActions}>{actions}</div>
            {onDismiss && <DismissButton onClick={onDismiss} className={layouts.barDismiss} />}
        </div>
    );
}
